import { useEffect, useState } from 'react';
import { EventInput } from '@fullcalendar/core';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { mapServiceProviderCalendarEvents } from '../_shared';
import { useServiceProviderUnits } from './use-service-provider-units';
import type { RecurrencePattern } from '../../../components/calendar/recurrence/recurrence-pattern';
import type { UpdateServiceProviderEventValues } from '../../citizen/serviceprovider/calendar/dialogs/event-dialog-types';

const calendarClient = moduleClientFactory.createServiceProviderUnitCalendarClient(true);

export function useGetServiceProviderUnitCalendarEvents(
  visibleRange: { start: Date; end: Date } | null
) {
  const { currentUnit } = useServiceProviderUnits();
  const serviceProviderUnitId = currentUnit?.id;
  const [events, setEvents] = useState<EventInput[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  async function loadCalendarEvents() {
    if (!serviceProviderUnitId || !visibleRange) {
      setEvents([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await calendarClient.getServiceProviderUnitCalendarEvents({
        serviceProviderUnitId,
        fromDate: visibleRange.start,
        toDate: visibleRange.end,
      });

      const loadedEvents = response.calendarEvents ?? [];
      setEvents(mapServiceProviderCalendarEvents(loadedEvents));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!serviceProviderUnitId || !visibleRange) {
      setEvents([]);
      return;
    }

    loadCalendarEvents();
  }, [serviceProviderUnitId, visibleRange?.start.getTime(), visibleRange?.end.getTime()]);

  async function createEvent(eventData: {
    serviceProviderUnitId: string;
    title: string;
    locationAddress: string;
    startAt: Date | string;
    endAt: Date | string;
    allDay: boolean;
    content?: string;
    responsibleUserId?: string;
    recurrence?: RecurrencePattern;
    noteTypeId?: string;
  }) {
    await calendarClient.createServiceProviderUnitCalendarEvent({
      serviceProviderUnitId: eventData.serviceProviderUnitId,
      title: eventData.title,
      locationAddress: eventData.locationAddress,
      responsibleUserId: eventData.responsibleUserId,
      startAt: eventData.startAt,
      endAt: eventData.endAt,
      allDay: eventData.allDay,
      content: eventData.content,
      recurrence: eventData.recurrence,
      noteTypeId: eventData.noteTypeId,
    });
    await loadCalendarEvents();
  }

  async function updateEvent(eventData: {
    eventId: string;
    title: string;
    locationAddress: string;
    startAt: Date | string;
    endAt: Date | string;
    allDay: boolean;
    content?: string;
    responsibleUserId?: string;
    consumedMinutes?: number;
    recurrence?: RecurrencePattern;
    noteTypeId?: string;
  }) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.updateServiceProviderUnitCalendarEvent({
      serviceProviderUnitId,
      ...eventData,
    });
    await loadCalendarEvents();
  }

  async function markDone(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.markServiceProviderUnitCalendarEventDone({
      serviceProviderUnitId,
      eventId,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function cancelEvent(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.cancelServiceProviderUnitCalendarEvent({
      serviceProviderUnitId,
      eventId,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function unmarkEvent(eventId: string) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.unmarkServiceProviderUnitCalendarEvent({
      serviceProviderUnitId,
      eventId,
    });
    await loadCalendarEvents();
  }

  async function deleteEvent(eventId: string) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.deleteServiceProviderUnitCalendarEvent({
      serviceProviderUnitId,
      eventId,
    });
    await loadCalendarEvents();
  }

  async function updateOccurrence(
    values: UpdateServiceProviderEventValues,
    occurrenceStartAt: Date
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.updateServiceProviderUnitCalendarSeriesOccurrence({
      serviceProviderUnitId,
      seriesId: values.eventId,
      occurrenceStartAt,
      title: values.title,
      locationAddress: values.locationAddress,
      responsibleUserId: values.responsibleUserId,
      startAt: values.startAt,
      endAt: values.endAt,
      allDay: values.allDay,
      content: values.content,
      consumedMinutes: values.consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function markOccurrenceDone(
    seriesId: string,
    occurrenceStartAt: Date,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.markServiceProviderUnitCalendarSeriesOccurrenceDone({
      serviceProviderUnitId,
      seriesId,
      occurrenceStartAt,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function cancelOccurrence(
    seriesId: string,
    occurrenceStartAt: Date,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.cancelServiceProviderUnitCalendarSeriesOccurrence({
      serviceProviderUnitId,
      seriesId,
      occurrenceStartAt,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function deleteOccurrence(seriesId: string, occurrenceStartAt: Date) {
    if (!serviceProviderUnitId) {
      return;
    }

    await calendarClient.deleteServiceProviderUnitCalendarSeriesOccurrence({
      serviceProviderUnitId,
      seriesId,
      occurrenceStartAt,
    });
    await loadCalendarEvents();
  }

  return {
    events,
    isLoading,
    createEvent,
    updateEvent,
    markDone,
    cancelEvent,
    unmarkEvent,
    deleteEvent,
    seriesOccurrenceCallbacks: {
      updateOccurrence,
      markOccurrenceDone,
      cancelOccurrence,
      deleteOccurrence,
    },
    reload: loadCalendarEvents,
  };
}
